// Short clip recording per attempt. Safari records H.264 MP4; Chromium may
// fall back to WebM.

const CANDIDATES = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];

export function recordingMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) ?? null;
}

interface Recording {
  rec: MediaRecorder;
  chunks: Blob[];
}

export class ClipRecorder {
  // Each recording owns its chunks, so discarding a new one can never corrupt
  // a previous clip that is still being finalised.
  private current: Recording | null = null;
  /** performance.now() when recording actually began. */
  startedAt = 0;

  constructor(private readonly stream: MediaStream, private readonly bitsPerSecond = 2_500_000) {}

  get active() {
    return this.current?.rec.state === 'recording';
  }

  async start(): Promise<number> {
    const mimeType = recordingMime();
    if (!mimeType) throw new Error('Recording isn’t supported in this browser.');
    const rec = new MediaRecorder(this.stream, { mimeType, videoBitsPerSecond: this.bitsPerSecond });
    const recording: Recording = { rec, chunks: [] };
    this.current = recording;
    rec.ondataavailable = (e) => {
      if (e.data.size) recording.chunks.push(e.data);
    };
    await new Promise<void>((resolve, reject) => {
      const fail = setTimeout(() => reject(new Error('Recorder did not start')), 3000);
      rec.onstart = () => {
        clearTimeout(fail);
        this.startedAt = performance.now();
        resolve();
      };
      // One segment per clip: Safari's fragmented-MP4 timeslices are the
      // riskier path, and clips are only a few seconds long.
      rec.start();
    });
    return this.startedAt;
  }

  async stop(): Promise<{ blob: Blob; mime: string } | null> {
    const recording = this.current;
    if (!recording) return null;
    this.current = null;
    const { rec, chunks } = recording;
    if (rec.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        const done = setTimeout(resolve, 3000); // never hang the practice loop
        rec.onstop = () => {
          clearTimeout(done);
          resolve();
        };
        rec.stop();
      });
    }
    const mime = rec.mimeType || recordingMime() || 'video/mp4';
    return chunks.length ? { blob: new Blob(chunks, { type: mime }), mime } : null;
  }

  /** Stops and throws away the recording in progress (if any). */
  discard(): void {
    const recording = this.current;
    this.current = null;
    if (recording && recording.rec.state !== 'inactive') recording.rec.stop();
  }
}

export function canvasToBlob(c: HTMLCanvasElement, quality = 0.82): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('Snapshot failed'))), 'image/jpeg', quality));
}
